import React from "react";
import { Vacation } from "domain/entities/vacations";
import { VacationStatusEnum } from "domain/enum/vacationStatus";
import useLongPress from "presentation/mobile/hooks/useLongPress";
import MobileActionButton from "presentation/mobile/components/MobileActionButton";
import { VACATION_STATUS_TONES, getStatusStyle } from "presentation/mobile/utils/status-styles.util";

interface VacationCardProps {
    vacation: Vacation;
    isExpanded: boolean;
    onToggle: () => void;
    onCancel: (vacation: Vacation) => void;
}

const VacationCard: React.FC<VacationCardProps> = ({
    vacation,
    isExpanded,
    onToggle,
    onCancel,
}) => {
    const { isPressing, handlers } = useLongPress({
        onLongPress: onToggle,
    });

    const formatDate = (date?: Date | null) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    // Estados 
    const getStatusLabel = (): string => {
        if (vacation.status === VacationStatusEnum.APROVADO && vacation.rejectionReason) return "Aprovado *";
        if (vacation.status === VacationStatusEnum.CANCELAMENTO_PENDENTE) return "Canc. Pendente";
        return vacation.status ?? "-";
    };

    const statusInfo = {
        label: getStatusLabel(),
        ...getStatusStyle(VACATION_STATUS_TONES, vacation.status),
    };

    const firstDayOfPreviousMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
    const isWithinCancelWindow = vacation.startAt
        ? new Date(vacation.startAt) >= firstDayOfPreviousMonth
        : false;

    const canCancel = vacation.status === VacationStatusEnum.A_APROVAR && isWithinCancelWindow;
    // Aprovado com rejectionReason = pedido de cancelamento já recusado pelo manager; a web não permite novo pedido
    const canRequestCancel =
        vacation.status === VacationStatusEnum.APROVADO &&
        !vacation.rejectionReason &&
        isWithinCancelWindow;

    const usesNextYearDays = Boolean(vacation.daysNextYear && vacation.daysNextYear > 0);

    return (
        <div
            {...handlers}
            className="border-round-xl p-3 flex flex-column gap-2 cursor-pointer"
            style={{
                backgroundColor: "#FFFFFF",
                border: isExpanded ? "1.5px solid #0084D1" : "1px solid #E2E8F0",
                boxShadow: isExpanded ? "0 4px 14px rgba(0, 132, 209, 0.08)" : "none",
                transform: isPressing ? "scale(0.99)" : "scale(1)",
                transition: "all 0.2s ease",
            }}
        >
            {/* Linha 1: Data da Solicitação + Badge + Botão '+' / '✕' */}
            <div className="w-full flex justify-content-between align-items-center">
                <div className="flex align-items-center gap-1 overflow-hidden pr-2">
                    {isExpanded && (
                        <span
                            style={{
                                color: "#0084D1",
                                fontSize: "1.1rem",
                                lineHeight: "1",
                                marginRight: "2px",
                            }}
                        >
                            •
                        </span>
                    )}
                    <span className="text-xs" style={{ color: "#64748B" }}>
                        Solicitado a: <strong style={{ color: "#0F172A" }}>{formatDate(vacation.createdAt)}</strong>
                    </span>
                </div>

                <div className="flex align-items-center gap-2 flex-shrink-0">
                    <span
                        className="badge-squircle"
                        style={{
                            color: statusInfo.color,
                            backgroundColor: statusInfo.bg,
                            border: `1px solid ${statusInfo.border}`,
                        }}
                    >
                        {statusInfo.label}
                    </span>

                    {/* Micro-interação: '+' gira 45º para '✕' */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                        className="border-none bg-transparent cursor-pointer flex align-items-center justify-content-center p-0"
                        style={{
                            width: "24px",
                            height: "24px",
                            color: "#94A3B8",
                            outline: "none",
                        }}
                        aria-label={isExpanded ? "Recolher" : "Expandir"}
                    >
                        <i
                            className="pi pi-plus text-xs"
                            style={{
                                transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Linha 2: Caixa Interior de Início -> Fim (Sem maiúsculas) */}
            <div
                className="w-full flex justify-content-between align-items-center px-3 py-2"
                style={{
                    backgroundColor: "#F8FAFC",
                    borderRadius: "10px",
                }}
            >
                <div className="flex flex-column gap-1">
                    <span
                        className="font-semibold text-xs"
                        style={{ color: "#64748B" }}
                    >
                        Início
                    </span>
                    <span className="text-sm font-bold" style={{ color: "#0F172A" }}>
                        {formatDate(vacation.startAt)}
                    </span>
                </div>

                <i className="pi pi-arrow-right text-xs" style={{ color: "#94A3B8" }} />

                <div className="flex flex-column align-items-end gap-1">
                    <span
                        className="font-semibold text-xs"
                        style={{ color: "#64748B" }}
                    >
                        Fim
                    </span>
                    <span className="text-sm font-bold" style={{ color: "#0F172A" }}>
                        {formatDate(vacation.endAt)}
                    </span>
                </div>
            </div>

            {/* Linha 3: Total de Dias Úteis */}
            <div className="w-full flex align-items-center gap-1">
                <i className="pi pi-clock text-xs" style={{ color: "#94A3B8" }} />
                <span className="text-xs font-medium" style={{ color: "#64748B" }}>
                    {vacation.days ?? 0} dias úteis
                </span>
            </div>

            {/* Aviso visual caso utilize dias do ano seguinte */}
            {usesNextYearDays && (
                <div
                    className="w-full flex align-items-center gap-2 px-2 py-1 border-round-md"
                    style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" }}
                >
                    <i className="pi pi-exclamation-triangle text-xs" style={{ color: "#D97706" }} />
                    <span className="text-xs font-medium" style={{ color: "#92400E" }}>
                        Utiliza {vacation.daysNextYear} {vacation.daysNextYear === 1 ? "dia" : "dias"} de férias do ano seguinte
                    </span>
                </div>
            )}

            {/* Secção Expandida: Motivo de Rejeição e Botões Reutilizáveis */}
            {isExpanded && (
                <div
                    className="w-full flex flex-column gap-2 pt-2 mt-1"
                    style={{ borderTop: "1px solid #F1F5F9" }}
                >
                    {/* Motivo de recusa exibido apenas se houver */}
                    {vacation.rejectionReason && vacation.status === VacationStatusEnum.NAO_APROVADO && (
                        <div
                            className="w-full p-2 border-round-md"
                            style={{ backgroundColor: "#FEF2F2", border: "1px solid #FEE2E2" }}
                        >
                            <span className="text-xs font-semibold text-red-700">
                                Motivo da não aprovação:{" "}
                            </span>
                            <span className="text-xs text-red-600">
                                {vacation.rejectionReason}
                            </span>
                        </div>
                    )}

                    {/* Aprovado com motivo */}
                    {vacation.rejectionReason && vacation.status === VacationStatusEnum.APROVADO && (
                        <div
                            className="w-full p-2 border-round-md flex align-items-start gap-2"
                            style={{ backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }}
                        >
                            <i className="pi pi-info-circle text-xs mt-1" style={{ color: "#0084D1"}} />
                            <span className="text-xs" style={{ color: "#075985" }}>
                                O colaborador solicitou o cancelamento destas férias mas foi rejeitado.{" "}
                            <strong>Motivo da rejeição:</strong> {vacation.rejectionReason}
                            </span>
                        </div>
                    )}

                    {/* Botões Reutilizáveis alinhados à direita */}
                    {(canCancel || canRequestCancel) && (
                        <div className="w-full flex justify-content-end gap-2 pt-1">
                            {canCancel && (
                                <MobileActionButton
                                    label="Cancelar"
                                    icon="pi pi-trash"
                                    severity="danger"
                                    onClick={() => onCancel(vacation)}
                                />
                            )}
                            {canRequestCancel && (
                                <MobileActionButton
                                    label="Pedir Cancelamento"
                                    icon="pi pi-times-circle"
                                    severity="secondary"
                                    onClick={() => onCancel(vacation)}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default VacationCard;